import { Chip, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { withStyles } from 'tss-react/mui';

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  pill: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    height: 24,
    '& .MuiChip-label': {
      padding: '0 10px'
    }
  },
  // Status-specific colors
  draftPill: {
    backgroundColor: theme.palette.grey[300],
    color: '#797979'
  },
  inReviewPill: {
    backgroundColor: "#D5E6FB",
    color: '#797979'
  },
  needsAttentionPill: {
    backgroundColor: "#F2C94C",
    color: '#797979'
  },
  approvedPill: {
    backgroundColor: "#CEF1DC",
    color: '#797979'
  },
  closedPill: {
    backgroundColor: theme.palette.grey[300],
    color: '#797979'
  }
});

type VcmStatus = 'draft' | 'in_review' | 'needs_attention' | 'approved' | 'closed';

interface StatusConfig {
  label: string;
  colorClass: keyof ReturnType<typeof styles>;
  additionalText: string | null;
}

const STATUS_CONFIG: Record<VcmStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    colorClass: 'draftPill',
    additionalText: 'with Educator'
  },
  in_review: {
    label: 'In Review',
    colorClass: 'inReviewPill',
    additionalText: 'with Supervisor'
  },
  needs_attention: {
    label: 'Needs Attention',
    colorClass: 'needsAttentionPill',
    additionalText: 'from Educator'
  },
  approved: {
    label: 'Approved',
    colorClass: 'approvedPill',
    additionalText: null
  },
  closed: {
    label: 'Closed',
    colorClass: 'closedPill',
    additionalText: null
  }
};

interface VcmStatusPillProps {
  classes: Record<keyof ReturnType<typeof styles>, string>;
  status: VcmStatus | string;
}

const VcmStatusPill: React.FC<VcmStatusPillProps> = ({ classes, status }) => {
  const config = STATUS_CONFIG[status as VcmStatus];
  
  if (!config) {
    // Fallback for unknown status
    return (
      <Typography fontSize={13} fontWeight={400} color='primary'>
        {status}
      </Typography>
    );
  }

  const pillClassName = `${classes.pill} ${classes[config.colorClass]}`;

  return (
    <div className={classes.container}>
      <Chip
        label={config.label}
        className={pillClassName}
        size="small"
      />
      {config.additionalText && (
        <Typography fontSize={13} fontWeight={400} color="primary">
          {config.additionalText}
        </Typography>
      )}
    </div>
  );
};

export default withStyles(VcmStatusPill, styles);